import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { Link } from "react-router-dom";
import { heroSlides, heroFeaturedProducts, press } from "@/mocks/home";

export default function Hero() {
  const root = useRef<HTMLDivElement>(null);
  const data = heroSlides[0];

  useEffect(() => {
    if (!root.current) return;
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ defaults: { ease: "expo.out" } });

      tl.from(
        ".hero-eyebrow",
        { y: 16, opacity: 0, duration: 0.8, ease: "power3.out" },
        0.1,
      )
        .from(
          ".hero-line",
          { scaleX: 0, duration: 1.2, ease: "expo.out" },
          0.2,
        )
        .from(
          ".hero-word",
          {
            yPercent: 110,
            opacity: 0,
            duration: 1.2,
            stagger: 0.12,
            ease: "power4.out",
          },
          0.4,
        )
        .from(
          ".hero-meta-col",
          { y: 20, opacity: 0, duration: 0.8, stagger: 0.1, ease: "power3.out" },
          0.85,
        )
        .from(
          ".hero-img-card",
          {
            y: 40,
            opacity: 0,
            duration: 1,
            stagger: 0.1,
            ease: "power3.out",
          },
          1.1,
        );
    }, root);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={root}
      className="relative min-h-screen w-full bg-bone text-ink overflow-hidden"
    >
      {/* Top meta row */}
      <div className="px-6 md:px-10 pt-24 md:pt-28 flex items-center justify-between">
        <span className="hero-eyebrow text-[11px] font-mono uppercase tracking-[0.22em] text-ink/50">
          Vol. 07 · Collection №24
        </span>
        <span className="hero-eyebrow hidden md:inline text-[11px] font-mono uppercase tracking-[0.22em] text-ink/50">
          Porto · 41.149° N
        </span>
      </div>

      {/* Massive headline */}
      <div className="px-6 md:px-10 mt-4 md:mt-6">
        <h1 className="font-display leading-[0.82] tracking-tighter text-ink">
          <span className="block overflow-hidden">
            <span className="hero-word inline-block text-[22vw] sm:text-[18vw] md:text-[15vw] lg:text-[14vw] font-semibold uppercase">
              {data.title}
            </span>
          </span>
        </h1>
      </div>

      {/* Divider line */}
      <div className="px-6 md:px-10 mt-4 md:mt-5">
        <div className="hero-line h-[2px] w-full bg-ink/40 origin-left"></div>
      </div>

      {/* Metadata columns */}
      <div className="px-6 md:px-10 mt-3 md:mt-5 grid grid-cols-2 md:grid-cols-4 gap-5 md:gap-8">
        <div className="hero-meta-col">
          <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-ink/40 mb-1.5">
            Label
          </p>
          <p className="text-[13px] text-ink/80 font-medium">Verdant</p>
        </div>
        <div className="hero-meta-col col-span-1 md:col-span-2">
          <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-ink/40 mb-1.5">
            About
          </p>
          <p className="text-[13px] text-ink/80 leading-relaxed max-w-md">
            {data.subtitle}
          </p>
        </div>
        <div className="hero-meta-col text-left md:text-right">
          <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-ink/40 mb-1.5">
            Links
          </p>
          <div className="flex flex-col gap-1">
            <Link
              to="/shop"
              className="text-[13px] text-ink/80 hover:text-rust transition-colors cursor-pointer whitespace-nowrap"
            >
              Visit the shop →
            </Link>
            <Link
              to="/sustainability"
              className="text-[13px] text-ink/80 hover:text-rust transition-colors cursor-pointer whitespace-nowrap"
            >
              Shipping & Returns
            </Link>
          </div>
          <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-ink/40 mt-3">
            © 2026
          </p>
        </div>
      </div>

      {/* 4-image grid */}
      <div className="px-6 md:px-10 mt-4 md:mt-5">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          {heroFeaturedProducts.map((product) => (
            <Link
              key={product.id}
              to="/shop"
              className="hero-img-card group block relative aspect-[2/3] rounded-2xl overflow-hidden"
            >
              <img
                src={product.image}
                alt={product.name}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/50 via-ink/10 to-transparent opacity-80 group-hover:opacity-100 transition-opacity"></div>
              <div className="absolute bottom-0 left-0 right-0 p-3 md:p-4">
                <p className="text-[11px] font-mono uppercase tracking-wider text-bone">
                  {product.name}
                </p>
                <p className="text-[11px] text-bone/70 mt-0.5">${product.price}</p>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Bottom editorial strip */}
      <div className="px-6 md:px-10 mt-4 md:mt-5 pb-10 md:pb-16">
        <div className="h-[1px] w-full bg-ink/20 mb-5 md:mb-6"></div>
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-5">
          <p className="text-[13px] text-ink/60 max-w-md leading-relaxed">
            Small-batch production from our Porto atelier. Each piece is numbered and tracked from soil to seam.
          </p>
          <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-6">
            <span className="text-[11px] font-mono uppercase tracking-[0.15em] text-ink/40">
              As seen in
            </span>
            <div className="flex flex-wrap gap-x-4 gap-y-1">
              {press.slice(0, 4).map((name) => (
                <span key={name} className="text-[12px] text-ink/50 font-medium">
                  {name}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}